import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '../../../shared-functions/LocalStorage';
import { passwordValidation } from '../../../shared-functions/Validation';
import auth from '../../../utils/api/auth';

/**
 * Initial password reset component
 * @returns {React.JSX.Element} The rendered Initial password reset component.
 * @description Initial password reset component is a component for initial password reset functionality.
 */
function InitialPasswordReset(): React.JSX.Element {
  const { t, i18n } = useTranslation('loginV2');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const accountVerificationStatus = getLocalStorageItem('accountVerificationStatus');
  const [credentials, setCredentials] = useState<{
    password: string;
    confirmPassword: string;
  }>({
    password: '',
    confirmPassword: '',
  });

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    hint: true,
    buttonLoader: false,
  });

  /**
   * Get input change value and store in credentials state
   * @param name
   * @param changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    setCredentials({
      ...credentials,
      [name]: changeValue,
    });
  };

  /**
   * Function to submit entered cre
   */
  const handleSubmit = () => {
    setFormStatus({
      showFormErrors: true,
      hint: false,
      buttonLoader: false,
    });
    if (
      passwordValidation(credentials.password) &&
      credentials.password === credentials.confirmPassword
    ) {
      setFormStatus({
        showFormErrors: true,
        hint: false,
        buttonLoader: true,
      });
      const requestBody = {
        newPassword: credentials.password,
      };
      auth
        .initialResetPassword(requestBody)
        .then(() => {
          setFormStatus({
            showFormErrors: false,
            hint: false,
            buttonLoader: false,
          });

          const userVerification = getLocalStorageItem('userVerification');

          let updatedUserVerification;
          if (userVerification?.redirectToEmailVerification) {
            updatedUserVerification = {
              ...userVerification,
              isInitialPasswordReset: true,
            };
            setLocalStorageItem('userVerification', updatedUserVerification);
            navigate('../login/email-verification');
          } else if (userVerification?.redirectToPhoneVerification) {
            updatedUserVerification = {
              ...userVerification,
              isInitialPasswordReset: true,
            };
            setLocalStorageItem('userVerification', updatedUserVerification);
            navigate('../login/phone-number-verification');
          } else {
            removeLocalStorageItem('userVerification'); // Remove user verification from local storage
            if (accountVerificationStatus === 'approved') {
              navigate('../ev-admin/dashboard');
            } else {
              navigate('../ev-admin/profile');
            }
          }
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: false,
            hint: false,
            buttonLoader: false,
          });
          // Display a user-friendly message
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || t('somethingWentWrong'),
              },
            }),
          );
        });
    }
  };

  return (
    <div className="login-component-v2" dir={i18n.dir()}>
      <div className="login-component-v2-body">
        <CustomInput
          inputLabel={t('createNewPassword')}
          type="password"
          placeholder={t('enterPassword')}
          name="password"
          value={credentials.password}
          onChangeValue={handleCredentialChange}
          error={!passwordValidation(credentials.password) && formStatus.showFormErrors}
          errorMessage={t('passwordValidationRequirements')}
          hint={
            credentials.password.length === 0 || !passwordValidation(credentials.password)
              ? formStatus.hint
              : false
          }
          hintMessage={t('passwordMustBeAtLeast12Char')}
        />
        <CustomInput
          inputLabel={t('confirmPassword')}
          type="password"
          placeholder={t('enterPassword')}
          name="confirmPassword"
          value={credentials.confirmPassword}
          onChangeValue={handleCredentialChange}
          error={
            !(credentials.password === credentials.confirmPassword) && formStatus.showFormErrors
          }
          errorMessage={t('passwordAndConfirmMustMatch')}
        />
        <MyButton
          label={t('submit')}
          buttonType="submit"
          onClickFunc={handleSubmit}
          loading={formStatus.buttonLoader}
          disableButton={formStatus.buttonLoader}
        />
      </div>
    </div>
  );
}

export default InitialPasswordReset;
